'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { LocalizedString } from '@/shared/model/Language';
import { Button } from '@/shared/ui/button';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './age-verification-modal.scss';

type Props = {
    onConfirm?(): void;
    onAbort?(): void;
    customTitle?: LocalizedString;
    customDescription?: LocalizedString;
    age?: number;
    children?: ReactNode;
} & ModalProps;

type AgeVerificationModalContextValue = {
    onClose: ModalProps['onClose'];
    onConfirm?: () => void;
    onAbort?: () => void;
    customTitle?: LocalizedString;
    customDescription?: LocalizedString;
    age?: number;
};

const AgeVerificationModalContext = createContext<AgeVerificationModalContextValue | null>(null);

const useAgeVerificationModalContext = () => {
    const context = useContext(AgeVerificationModalContext);
    if (!context) {
        throw new Error('AgeVerificationModal components must be used within AgeVerificationModalWrapper.');
    }
    return context;
};

export const AgeVerificationModalWrapper = ({ onConfirm, onAbort, children, ...props }: Props) => {
    return (
        <AgeVerificationModalContext.Provider value={{ onClose: props.onClose, onConfirm, onAbort }}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'age-verification-modal',
                    'data-testid': 'age-verification-modal',
                }}
            >
                {children}
            </Modal>
        </AgeVerificationModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const AgeVerificationModalProvider = ({
    onConfirm,
    onAbort,
    customTitle,
    customDescription,
    age,
    children,
    ...props
}: Props) => {
    return (
        <AgeVerificationModalContext.Provider
            value={{ onClose: props.onClose, onConfirm, onAbort, customTitle, customDescription, age }}
        >
            {children}
        </AgeVerificationModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const AgeVerificationModalTitle = () => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const { customTitle, age } = useAgeVerificationModalContext();

    const localizedTitle = customTitle ? getLocalizedValue(customTitle) : '';
    return <>{localizedTitle || t('AGE_VERIFICATION_MODAL.TITLE', { age: age || 18 })}</>;
};

/** Renders only the description text. Use inside prefab: "Modal.Description". */
const AgeVerificationModalDescription = () => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const { customDescription, age } = useAgeVerificationModalContext();

    const localizedDescription = customDescription ? getLocalizedValue(customDescription) : '';
    return <>{localizedDescription || t('AGE_VERIFICATION_MODAL.DESCRIPTION', { age: age || 18 })}</>;
};

type ActionButtonProps = ComponentProps<typeof Button>;

const AgeVerificationModalNoButton = ({ onClick, ...props }: ActionButtonProps) => {
    const t = useTranslations();
    const { onClose, onAbort } = useAgeVerificationModalContext();

    return (
        <Layout
            element={Button}
            variant="secondary"
            data-testid="age-verification-modal-no-button"
            onClick={(event) => {
                onClick?.(event);
                onClose();
                onAbort?.();
            }}
            {...props}
        >
            {t('WORDS.NO')}
        </Layout>
    );
};

const AgeVerificationModalYesButton = ({ onClick, ...props }: ActionButtonProps) => {
    const t = useTranslations();
    const { onClose, onConfirm } = useAgeVerificationModalContext();

    // Don't render if no onConfirm callback (scenario 3: underage)
    if (!onConfirm) {
        return null;
    }

    return (
        <Layout
            element={Button}
            data-testid="age-verification-modal-yes-button"
            onClick={(event) => {
                onClick?.(event);
                onClose();
                onConfirm?.();
            }}
            {...props}
        >
            {t('WORDS.YES')}
        </Layout>
    );
};

export { AgeVerificationModalWrapper as AgeVerificationModal };

export {
    AgeVerificationModalWrapper as Wrapper,
    AgeVerificationModalProvider as Provider,
    AgeVerificationModalTitle as Title,
    AgeVerificationModalDescription as Description,
    AgeVerificationModalNoButton as NoButton,
    AgeVerificationModalYesButton as YesButton,
};
